import type { PlayerRole } from '@/store/useGameStore';

/**
 * Game system declared in the team tactics (VOL-162, DOMAIN_RULES « Système de jeu »).
 * Absent = classic 5-1. Same list validated by the server (`TeamTacticsDto`).
 */
export const GAME_SYSTEMS = ['5-1', '5-1-3R4', '5-1-3C', '5-1-1C', '4-2', '6-2'] as const;
export type GameSystem = (typeof GAME_SYSTEMS)[number];
export const DEFAULT_GAME_SYSTEM: GameSystem = '5-1';

const GAME_SYSTEM_SET: ReadonlySet<string> = new Set(GAME_SYSTEMS);

export function isGameSystem(value: unknown): value is GameSystem {
  return typeof value === 'string' && GAME_SYSTEM_SET.has(value);
}

/** Systems with two setters on court: one sets, the other plays opposite. */
export function hasTwoSetters(system: GameSystem | undefined): boolean {
  return system === '4-2' || system === '6-2';
}

const FRONT_ROW = [1, 2, 3] as const;
const BACK_ROW = [0, 5, 4] as const;

interface CourtRolesInput<R extends string = PlayerRole> {
  court: readonly string[];
  courtRoles: readonly R[];
  playerRoles: Readonly<Record<string, string>>;
}

function isSetterAt({ court, courtRoles, playerRoles }: CourtRolesInput<string>, i: number): boolean {
  return courtRoles[i] === 'Passeur' || playerRoles[court[i]] === 'Passeur';
}

/**
 * The designated setter went off and no `Passeur` is left on court: another court
 * player whose roster role is `Passeur` becomes the designated setter, so the
 * rotation follows him instead of falling back to P1 (6-2, VOL-162). Mutates
 * `courtRoles`; same rule on the server replay.
 */
export function promoteRemainingSetter(
  court: readonly string[],
  courtRoles: string[],
  playerRoles: Readonly<Record<string, string>>,
  setterWentOff: boolean,
): void {
  if (!setterWentOff || courtRoles.slice(0, 6).includes('Passeur')) return;
  const idx = court.slice(0, 6).findIndex((name) => playerRoles[name] === 'Passeur');
  if (idx !== -1) courtRoles[idx] = 'Passeur';
}

/**
 * Court index (0-5) of the player who sets this rally, -1 if none.
 *
 * The rotation keeps following the designated setter (`courtRoles` 'Passeur', one
 * per court): only who sets changes. In a 6-2 the back-row setter sets, in a 4-2
 * the front-row one; with a single setter on court, he sets whatever the system.
 */
export function rallySetterIndex(input: CourtRolesInput<string>, system: GameSystem | undefined): number {
  const designated = input.courtRoles.slice(0, 6).indexOf('Passeur');
  if (!hasTwoSetters(system)) return designated;
  const row = system === '6-2' ? BACK_ROW : FRONT_ROW;
  const inRow = row.find((i) => isSetterAt(input, i));
  return inRow ?? designated;
}

/**
 * Roles used to place the players and to suggest them, by court index (0-5 and the
 * libero slot): the role each one actually plays this rally under the system.
 *
 * - 4-2 / 6-2: the setter of the rally is `Passeur`, the other setter plays opposite
 *   (`Pointu`). `designateSetter` stores him as `R4`, which sent him to zone 4.
 * - 5-1 with three outside hitters: the `R4` opposite the setter plays opposite.
 * - 5-1 with three middles: the `Central` opposite the setter plays opposite.
 * - 5-1 with one middle: the player opposite the only `Central` plays middle.
 */
export function playingRoles<R extends string = PlayerRole>(
  input: CourtRolesInput<R>,
  system: GameSystem | undefined,
): (R | 'Passeur' | 'Pointu' | 'Central')[] {
  const roles: (R | 'Passeur' | 'Pointu' | 'Central')[] = [...input.courtRoles];
  const setter = rallySetterIndex(input, system);
  if (setter === -1) return roles;
  if (hasTwoSetters(system)) {
    for (let i = 0; i < 6; i++) {
      if (i !== setter && isSetterAt(input, i)) roles[i] = 'Pointu';
    }
    roles[setter] = 'Passeur';
  } else if (system === '5-1-3R4' || system === '5-1-3C') {
    const opposite = (setter + 3) % 6;
    if (roles[opposite] === (system === '5-1-3R4' ? 'R4' : 'Central')) roles[opposite] = 'Pointu';
  } else if (system === '5-1-1C') {
    const centrals = roles.slice(0, 6).flatMap((r, i) => (r === 'Central' ? [i] : []));
    if (centrals.length === 1) {
      const opposite = (centrals[0] + 3) % 6;
      if (roles[opposite] !== 'Passeur') roles[opposite] = 'Central';
    }
  }
  return roles;
}
